import React, { useCallback, useMemo, useContext, useState } from 'react';
import _ from 'lodash';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import { translate as t } from 'hooks/useTranslations';
import GridSection from 'components/_layouts/GridSection';
import { useFields } from '../_data_sources/useFields';
import Modal from 'components/_layouts/Modal';
import { Box, Button, Typography, CircularProgress, Stack, TextField } from '@mui/material';
import PageToolbar from '_lex/_atoms/PageToolbar';
import PhxBadge from '_lex/_atoms/PhxBadge';
import { PhxBadgeCounter } from '_lex/_atoms/PhxBadgeCounter/PhxBadgeCounter';
import { useNavigate } from 'react-router-dom';
import { AppContext } from 'views/App/context';
import { QuickSearch, ActionMenu } from 'components/_atoms';
import useUrlParams from 'hooks/useUrlParams';
import { FieldStatus, FieldTypeEnum } from '../_types/fieldbank';
import FieldBankFilters from './FieldBankFilters';
import FiltersGroup from './FiltersGroup';
import { useConfirm } from 'material-ui-confirm';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import useManageFields from '../_data_sources/useManageFields';
import { ServerErrorResponse } from 'data_sources/constants';

export type FieldBankParams = {
  search?: string | null;
  status?: FieldStatus | null;
  fieldTypes?: FieldTypeEnum[] | null;
  fieldCategoryIds?: number[] | null;
};

const defaultParams: FieldBankParams = {
  search: null,
  status: null,
  fieldTypes: null,
  fieldCategoryIds: null,
};

const FieldBankList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const [params, setParams] = useUrlParams(defaultParams);
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { deactivate } = useManageFields();
  const [deactivatingFields, setDeactivatingFields] = useState<Set<number>>(new Set());
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [deactivateFieldId, setDeactivateFieldId] = useState<number | null>(null);
  const [deactivateChangeDescription, setDeactivateChangeDescription] = useState('');

  const updateParams = useCallback(
    (updatedParams: FieldBankParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const cleanParams = _.omitBy({ ...params }, _.isEmpty);

    // Convert arrays to comma-separated strings for API
    const processedParams = Object.fromEntries(
      Object.entries(cleanParams).map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value])
    );

    return processedParams;
  }, [params]);

  const isFiltered = !_.isEmpty(queryParams);

  const fieldsQuery = useFields(queryParams);
  const allFields = fieldsQuery.data?.pages.flatMap((page) => page.content) || [];
  const totalCount = fieldsQuery.data?.pages[0]?.count || 0;
  const { isLoading, isError, error } = fieldsQuery;

  const canManageFieldBank = hasPermission('manageFieldBank', currentOrganizationId || '');

  const onSearchChange = useCallback(
    (val: string) => {
      updateParams({ search: val?.trim() });
    },
    [updateParams]
  );

  const handleDeactivate = useCallback(async (fieldId: number) => {
    // Show change log modal for deactivation
    setDeactivateFieldId(fieldId);
    setDeactivateChangeDescription('');
    setIsDeactivateModalOpen(true);
  }, []);

  const handleDeactivateConfirm = useCallback(async () => {
    if (!deactivateFieldId || !deactivateChangeDescription.trim()) return;

    setDeactivatingFields((prev) => new Set(prev).add(deactivateFieldId));

    try {
      await deactivate(
        deactivateFieldId,
        deactivateChangeDescription.trim(),
        () => {
          enqueueSnackbar(t('performanceReporting.fieldBank.details.actions.deactivateSuccess'), {
            variant: 'success',
            autoHideDuration: 3000,
          });
          // Invalidate both the fields list and the individual field details
          queryClient.invalidateQueries(['fields']);
          queryClient.invalidateQueries(['field', deactivateFieldId, true]);
          queryClient.invalidateQueries(['fieldAuditLog', deactivateFieldId]);
          setDeactivatingFields((prev) => {
            const newSet = new Set(prev);
            newSet.delete(deactivateFieldId);
            return newSet;
          });
          setIsDeactivateModalOpen(false);
          setDeactivateFieldId(null);
          setDeactivateChangeDescription('');
        },
        (error: ServerErrorResponse) => {
          enqueueSnackbar(t('performanceReporting.fieldBank.details.actions.deactivateError'), {
            variant: 'error',
            autoHideDuration: 5000,
          });
          setDeactivatingFields((prev) => {
            const newSet = new Set(prev);
            newSet.delete(deactivateFieldId);
            return newSet;
          });
        }
      );
    } catch (error: any) {
      console.error('Error deactivating field:', error);
      enqueueSnackbar(t('performanceReporting.fieldBank.details.actions.deactivateError'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
      setDeactivatingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(deactivateFieldId);
        return newSet;
      });
    }
  }, [deactivateFieldId, deactivateChangeDescription, deactivate, queryClient, t]);

  const handleDeactivateCancel = useCallback(() => {
    setIsDeactivateModalOpen(false);
    setDeactivateFieldId(null);
    setDeactivateChangeDescription('');
  }, []);

  const columns = useMemo(
    () => ({
      name: {
        header: t('performanceReporting.fieldBank.list.columns.name'),
        flex: 2,
        renderer: (params: any) => (
          <Typography
            variant='body1'
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={() => navigate(`./${params.row.id}`)}
          >
            {params.row.name}
          </Typography>
        ),
      },
      label: {
        header: t('performanceReporting.fieldBank.list.columns.label'),
        flex: 2,
        renderer: (params: any) => <Typography variant='body1'>{params.row.label}</Typography>,
      },
      category: {
        header: t('performanceReporting.fieldBank.list.columns.category'),
        flex: 2,
        renderer: (params: any) => <Typography variant='body1'>{params.row.fieldCategory?.name || ''}</Typography>,
      },
      fieldType: {
        header: t('performanceReporting.fieldBank.list.columns.fieldType'),
        flex: 1.5,
        renderer: (params: any) => (
          <Typography variant='body1'>
            {t(`performanceReporting.fieldBank.fieldType.${params.row.fieldType}`) || params.row.fieldType || ''}
          </Typography>
        ),
      },
      templates: {
        header: t('performanceReporting.fieldBank.list.columns.templates'),
        flex: 2,
        renderer: (params: any) => (
          <PhxBadgeCounter
            number={params.row.templates ? params.row.templates.length : 0}
            color='gray'
            size='sm'
          />
        ),
      },
      status: {
        header: t('performanceReporting.fieldBank.list.columns.status'),
        flex: 1,
        renderer: (params: any) => {
          const status = params.row.status;
          const statusLower = status?.toLowerCase() || '';

          let color: 'success' | 'error' | 'default' = 'default';
          if (statusLower === 'active') color = 'success';
          else if (statusLower === 'draft') color = 'default';
          else if (statusLower === 'inactive') color = 'error';

          return (
            <PhxBadge
              color={color}
              size='sm'
            >
              {status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : 'Unknown'}
            </PhxBadge>
          );
        },
      },
      actions: {
        header: '',
        flex: 0.3,
        renderer: (params: any) => {
          const field = params.row;
          const isDeactivating = deactivatingFields.has(field.id);
          const canDeactivate = canManageFieldBank && field.status?.toLowerCase() === 'active';

          const actionItems = [
            {
              key: `view_details_${field.id}`,
              label: t('performanceReporting.fieldBank.list.actions.viewDetails'),
              onClick: () => navigate(`./${field.id}`),
              disabled: isDeactivating,
            },
          ];

          if (canDeactivate) {
            actionItems.push({
              key: `deactivate_${field.id}`,
              label: isDeactivating
                ? t('performanceReporting.fieldBank.details.actions.deactivating')
                : t('performanceReporting.fieldBank.list.actions.deactivate'),
              onClick: () => handleDeactivate(field.id),
              disabled: isDeactivating,
            });
          }

          return (
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
            >
              {isDeactivating && <CircularProgress size={16} />}
              <ActionMenu
                id={`field_action_menu_${field.id}`}
                btnProps={{
                  disabled: isDeactivating,
                }}
                items={actionItems}
              />
            </Stack>
          );
        },
      },
    }),
    [t, navigate, canManageFieldBank, deactivatingFields, handleDeactivate]
  );

  return (
    <CommonLayout
      pageTitle={t('performanceReporting.fieldBank.list.title')}
      headerProps={{
        title: t('performanceReporting.fieldBank.list.title'),
        subtitle: t('performanceReporting.fieldBank.list.subtitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <Stack
              direction='row'
              spacing={3}
              sx={{
                alignItems: 'center',
                '& > *': {
                  minWidth: 'fit-content',
                  flexShrink: 0,
                },
              }}
            >
              <QuickSearch
                id='fields_search'
                initialValue={params.search || ''}
                onChange={onSearchChange}
                placeholder={t('performanceReporting.fieldBank.list.search.placeholder')}
              />
              <FieldBankFilters
                params={params}
                updateParams={(newFilter) => updateParams(newFilter)}
              />
            </Stack>
          }
          rightItems={
            <Stack
              direction='row'
              spacing={2}
              sx={{ alignItems: 'center', display: 'flex' }}
            >
              {canManageFieldBank && (
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => navigate('./new')}
                >
                  {t('performanceReporting.fieldBank.list.createField')}
                </Button>
              )}
            </Stack>
          }
        />
        <FiltersGroup
          params={params}
          updateParams={updateParams}
          clearParams={clearParams}
        />
        {isLoading ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>Error loading fields</Box>
        ) : (
          <GridSection
            columns={columns}
            data={allFields}
            totalCount={totalCount}
            query={fieldsQuery}
            gridKey={JSON.stringify(queryParams)}
          />
        )}

        {/* Deactivation Change Log Modal */}
        <Modal
          open={isDeactivateModalOpen}
          onClose={handleDeactivateCancel}
          title={t('performanceReporting.fieldBank.details.deactivateConfirm.title')}
          size='sm'
          actions={
            <>
              <Button
                color='neutral'
                onClick={handleDeactivateCancel}
                disabled={deactivatingFields.has(deactivateFieldId || 0)}
              >
                {t('performanceReporting.fieldBank.details.deactivateConfirm.cancel')}
              </Button>
              <Button
                variant='contained'
                color='error'
                onClick={handleDeactivateConfirm}
                disabled={!deactivateChangeDescription.trim() || deactivatingFields.has(deactivateFieldId || 0)}
                loading={deactivatingFields.has(deactivateFieldId || 0)}
                loadingPosition='end'
              >
                {t('performanceReporting.fieldBank.details.deactivateConfirm.confirm')}
              </Button>
            </>
          }
        >
          <Stack spacing={2}>
            <Typography
              variant='body1'
              sx={{ pt: 4 }}
            >
              {t('performanceReporting.fieldBank.details.deactivateConfirm.message')}
            </Typography>
            <TextField
              autoFocus
              margin='dense'
              id='deactivateChangeDescription'
              label={t('performanceReporting.fieldBank.modal.noteLabel')}
              type='text'
              fullWidth
              variant='outlined'
              value={deactivateChangeDescription}
              onChange={(e) => setDeactivateChangeDescription(e.target.value)}
              error={false}
              multiline
              minRows={2}
            />
          </Stack>
        </Modal>
      </Box>
    </CommonLayout>
  );
};

export default FieldBankList;
